import type { LucideIcon } from "lucide-react";
import { BarChart3, GraduationCap, SlidersHorizontal } from "lucide-react";
import { ROUTES } from "./routes";

/**
 * Stable in-page anchors on Settings that "what's new" items (and anything else) can deep
 * link to via `/settings#<anchor>`. Settings scrolls to the matching element once loaded.
 */
export const SETTINGS_ANCHORS = {
  notifications: "notifications",
  trackedFields: "tracked-fields",
  ovulationTestField: "field-ovulation-test",
  importData: "import-data",
  instructorSharing: "instructor-sharing",
} as const;

export type SettingsAnchor =
  (typeof SETTINGS_ANCHORS)[keyof typeof SETTINGS_ANCHORS];

export const settingsAnchorRoute = (anchor: SettingsAnchor) =>
  `${ROUTES.settings}#${anchor}`;

export type WhatsNewItemId =
  | "v050_insights_chart"
  | "v050_tracked_fields"
  | "v050_instructor_linked"
  | "v050_instructor_invite";

/**
 * Who an item applies to. Evaluated against the signed-in account's owner context:
 * - `any`: anyone with their own (or a spouse-shared) chart — i.e. not an instructor.
 * - `tracking`: the tracking account itself (the only role that sees Settings → tracked fields
 *   and Settings → Instructor Sharing).
 * - `write`: anyone who can write the owner's observations (tracking owner or write-link spouse).
 */
export type WhatsNewAudience = "any" | "tracking" | "write";

/** What the CTA does: go to an in-app route (may carry a `#anchor`), or open support chat. */
export type WhatsNewAction =
  | { kind: "route"; route: string }
  | { kind: "support" };

export type WhatsNewItem = {
  id: WhatsNewItemId;
  title: string;
  description: string;
  icon: LucideIcon;
  ctaLabel: string;
  action: WhatsNewAction;
  audience: WhatsNewAudience;
  /**
   * Only show when the account does / doesn't already share its chart with an instructor.
   * Hidden while that's unknown (still loading, or the lookup failed).
   */
  instructorLink?: "linked" | "none";
  /**
   * Visiting any of these paths (pathname only, no hash) counts as "seeing" the feature and
   * clears its New badge. Omit for items whose destination is shared with unrelated content
   * (e.g. Settings) — those clear on CTA click or dismiss instead.
   */
  visitPaths?: string[];
  /** Hide the item when an optional tracking field is already switched on. */
  hideWhenFieldEnabled?: string;
  /**
   * Listed and completable like any other card, but not required to complete the release step
   * (e.g. the instructor cards, which not everyone acts on).
   */
  optional?: boolean;
};

export type WhatsNewReleaseNotesSection = {
  /** e.g. "Improvements", "Fixes". */
  heading: string;
  /** Short, plain-language bullets for users (no internals). */
  items: string[];
};

export type WhatsNewRelease = {
  /** Full three-part version, shown as-is ("What's new in 0.5.0"). */
  id: string;
  /** Feature cards at the top of the dialog, each with a CTA and "Mark as done". */
  items: WhatsNewItem[];
  /** Plain-text release notes shown under the feature cards. */
  notes: WhatsNewReleaseNotesSection[];
};

/**
 * The latest release, shown as the permanent "Review the latest release" step on Getting
 * Started. For a new release, replace this object (new `id`, new item ids, new notes): the step
 * becomes un-completed again for everyone, and the New badge / nav dot / home card come back
 * until they complete it.
 */
export const WHATS_NEW_RELEASE: WhatsNewRelease = {
  id: "0.5.0",
  items: [
    {
      id: "v050_insights_chart",
      title: "See your new Insights chart",
      description:
        "Your cycle on one chart: temperatures with monitor, ovulation test, mucus and bleeding lanes, fertile window shading, full-screen view and a side-by-side cycle comparison.",
      icon: BarChart3,
      ctaLabel: "Open Insights",
      action: { kind: "route", route: ROUTES.dashboard },
      audience: "any",
      visitPaths: [ROUTES.dashboard],
    },
    {
      id: "v050_tracked_fields",
      title: "Choose what you track",
      description:
        "Revisit which optional fields show up when you log a day — monitor readings, mucus color and frequency, and the new Ovulation test (OPK) field for LH strips. Switch on what you use, then tap Save Field Preferences.",
      icon: SlidersHorizontal,
      ctaLabel: "Review tracked fields",
      action: {
        kind: "route",
        route: settingsAnchorRoute(SETTINGS_ANCHORS.trackedFields),
      },
      audience: "tracking",
    },
    {
      id: "v050_instructor_linked",
      title: "Your instructor can see the new charts",
      description:
        "Instructors you share with see the new Insights chart on your data too — let them know. Does your instructor want their own instructor account? Reach out to us and we'll set them up.",
      icon: GraduationCap,
      ctaLabel: "Contact us",
      action: { kind: "support" },
      audience: "tracking",
      instructorLink: "linked",
      optional: true,
    },
    {
      id: "v050_instructor_invite",
      title: "Working with an NFP instructor?",
      description:
        "Invite them to view your chart. They get read-only access to your calendar, Insights and history, and you can remove access at any time.",
      icon: GraduationCap,
      ctaLabel: "Invite your instructor",
      action: {
        kind: "route",
        route: settingsAnchorRoute(SETTINGS_ANCHORS.instructorSharing),
      },
      audience: "tracking",
      instructorLink: "none",
      optional: true,
    },
  ],
  notes: [
    {
      heading: "New",
      items: [
        "A redesigned Insights page: one cycle chart with your temperatures and lanes for monitor, ovulation test, mucus and bleeding, fertile window shading, a full-screen view, and a card to compare cycles side by side.",
        "Optional Ovulation test (OPK) field for logging positive or negative LH strips. It's off by default — turn it on in Settings → tracked fields. Your first positive shows as the LH surge on your chart.",
        "Share your chart with an NFP instructor from Settings → Instructor Sharing. Instructors get read-only access, and you can remove it at any time.",
        "Edit your first and last name yourself in Settings.",
        "Restore or move your data: import a CSV you exported from NFP Tracker (Settings → Import Data).",
      ],
    },
    {
      heading: "Improvements",
      items: [
        "Temperatures keep two decimal places, so 96.67°F stays 96.67 instead of rounding to 96.7.",
        "More accurate fertile window and Peak Day for Creighton, Billings and Sympto-Thermal charting. If you use one of these methods, your Peak Day may now show a few days later than before.",
        "Better handling of cycles without a marked start: days before your first recorded cycle start are shown by date, and we'll suggest marking Cycle start when a new cycle looks likely.",
        "Entries you log ahead of time no longer make a future cycle show as your current one.",
        "CSV export now includes everything you log, including temperature and cycle day, and covers your full history.",
      ],
    },
    {
      heading: "Fixes",
      items: [
        "Insights charts now fit on phone screens instead of running off the edge.",
        "PDF export: cycle lengths now match Cycle History and Insights, dates no longer print a day early in some time zones, and long histories are no longer cut off.",
        'The Help page\'s answer to "Is my data private and secure?" now clearly explains who can see your chart.',
      ],
    },
  ],
};
